import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  NavigationConfig,
  NavigationOptions,
  NavigationEvent
} from './interfaces/navigation.interfaces';

/**
 * Generic navigation link component with ship icon support
 * Core component that provides consistent navigation patterns across the application
 */
@Component({
  selector: 'app-navigation-link',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: './navigation-link.component.html',
  styleUrls: ['./navigation-link.component.scss']
})
export class NavigationLinkComponent<TData = any, TQuery = any> implements OnInit {
  /** Text to display in the navigation link */
  @Input() text: string = '';

  /** Whether the link is navigable/clickable */
  @Input() isNavigable: boolean = false;

  /** Navigation configuration including data, url, and query params */
  @Input() navigationConfig: NavigationConfig<TData, TQuery>;

  /** Icon to display (defaults to ship/rocket-launch) */
  @Input() icon: string = 'heroicons_outline:rocket-launch';

  /** Position of icon relative to text */
  @Input() iconPosition: 'left' | 'right' = 'right';

  /** Whether to show the icon */
  @Input() showIcon: boolean = true;

  /** Whether to show the text */
  @Input() showText: boolean = true;

  /** Which parts are clickable */
  @Input() interactionMode: 'both' | 'text-only' | 'icon-only' = 'both';

  /** Custom CSS classes */
  @Input() customClass: string = '';

  /** Tooltip text to display */
  @Input() tooltipText: string = '';

  /** Event emitted when navigation occurs */
  @Output() navigate = new EventEmitter<NavigationEvent<TData, TQuery>>();

  /**
   * Handle navigation click
   */
  handleNavigation(): void {
    if (!this.isNavigable || !this.navigationConfig) {
      return;
    }

    const navigationEvent: NavigationEvent<TData, TQuery> = {
      config: this.navigationConfig,
      timestamp: new Date(),
      source: 'navigation-link'
    };

    this.navigate.emit(navigationEvent);
  }

  /**
   * Handle text-only click (when interactionMode is 'text-only' or 'both')
   */
  handleTextClick(): void {
    if (this.interactionMode === 'icon-only') {
      return;
    }
    this.handleNavigation();
  }

  /**
   * Handle icon-only click (when interactionMode is 'icon-only' or 'both')
   */
  handleIconClick(): void {
    if (this.interactionMode === 'text-only') {
      return;
    }
    this.handleNavigation();
  }

  /**
   * Get CSS classes for the navigation container
   */
  getContainerClasses(): string {
    const baseClasses = 'flex items-center';
    const interactionClasses = this.isNavigable
      ? 'cursor-pointer transition-colors'
      : '';
    const customClasses = this.customClass || '';

    return [baseClasses, interactionClasses, customClasses]
      .filter(cls => cls.length > 0)
      .join(' ');
  }

  /**
   * Get CSS classes for the text element
   */
  getTextClasses(): string {
    const baseClasses = '';
    const interactionClasses = this.isNavigable &&
      (this.interactionMode === 'both' || this.interactionMode === 'text-only')
      ? 'underline hover:text-blue-600'
      : '';

    return [baseClasses, interactionClasses]
      .filter(cls => cls.length > 0)
      .join(' ');
  }

  /**
   * Get CSS classes for the icon element
   */
  getIconClasses(): string {
    const baseClasses = 'icon-size-3';
    const positionClasses = this.iconPosition === 'left' ? 'mr-2' : 'ml-2';
    const interactionClasses = this.isNavigable
      ? 'hover:animate-bounce'
      : '';

    return [baseClasses, positionClasses, interactionClasses]
      .filter(cls => cls.length > 0)
      .join(' ');
  }

  /**
   * Check if text click should be allowed
   */
  isTextClickable(): boolean {
    return this.isNavigable &&
           (this.interactionMode === 'both' || this.interactionMode === 'text-only');
  }

  /**
   * Check if icon click should be allowed
   */
  isIconClickable(): boolean {
    return this.isNavigable &&
           (this.interactionMode === 'both' || this.interactionMode === 'icon-only');
  }

  /**
   * Validate input configuration
   * Warns about invalid combinations
   */
  ngOnInit(): void {
    this.validateConfiguration();
  }

  /**
   * Validate component configuration
   */
  private validateConfiguration(): void {
    // Check if both icon and text are hidden
    if (!this.showIcon && !this.showText) {
      console.warn('NavigationLinkComponent: Both showIcon and showText are false. Component will not display anything.');
    }

    // Check if interaction mode doesn't match display
    if (this.interactionMode === 'icon-only' && !this.showIcon) {
      console.warn('NavigationLinkComponent: interactionMode is icon-only but showIcon is false.');
    }

    if (this.interactionMode === 'text-only' && !this.showText) {
      console.warn('NavigationLinkComponent: interactionMode is text-only but showText is false.');
    }

    // Check if text is empty when showText is true
    if (this.showText && !this.text?.trim()) {
      console.warn('NavigationLinkComponent: showText is true but text is empty.');
    }
  }

  /**
   * Check if the component has valid display configuration
   */
  hasValidDisplay(): boolean {
    return this.showIcon || (this.showText && !!this.text?.trim());
  }

  /**
   * Get appropriate cursor class for text
   */
  getTextCursorClass(): string {
    return this.isTextClickable() ? 'cursor-pointer' : '';
  }

  /**
   * Get appropriate cursor class for icon
   */
  getIconCursorClass(): string {
    return this.isIconClickable() ? 'cursor-pointer' : '';
  }

  /**
   * Check if text should have underline
   */
  shouldUnderlineText(): boolean {
    return this.isTextClickable();
  }
}